import { queryClient } from "App";
import { get } from "lodash";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getEmailFooterTableColumns,
  getEmailFooterTableFilters,
} from "routes/email/components/EmailFooterTableColumnsAndFilters";
import { EMAIL_FOOTER_TABLE_CONSTANTS } from "routes/email/EmailFooterTableV3";
import TagService from "services/Tag";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import tagConstants from "constants/Tag";

export const invalidateEmailFooterTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [EMAIL_FOOTER_TABLE_CONSTANTS.queryKey],
  });
};

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

export const useEmailFooterTable = ({ className = null, tableName = null }) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", className],
    queryFn: () => fetchTags(className),
  });
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isTagsFetched) {
      return [];
    }
    return getEmailFooterTableFilters({
      tags,
    });
  }, [tags, isTagsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getEmailFooterTableColumns();

    const emailFooterTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: emailFooterTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns, tags };
};
